/**
 * TimeRuler — vertical time ruler for the Structured A/V view. Renders integer-
 * second tick labels (0s, 1s, 2s, ...) at `top: i * PIXELS_PER_SECOND`, plus
 * a minor tick at the half-second mark. The same `PIXELS_PER_SECOND` constant
 * is used by absolute-positioned blocks so video / dialogue / SFX blocks line
 * up with the ruler on a SHARED timeline.
 */

import { memo } from 'react'

/** Visual scale: how many pixels represent one second on the timeline.
 * Picked so a typical 8s Veo 3.1 clip fits ~320px tall, which matches the
 * existing Unified Bottom Panel comfortably without forcing tiny labels. */
export const PIXELS_PER_SECOND = 40

interface TimeRulerProps {
  maxSec: number
}

export const TimeRuler = memo(function TimeRuler({ maxSec }: TimeRulerProps) {
  const ticks: number[] = []
  // Render an integer tick at every second from 0 to ceil(maxSec). At least 1
  // tick (0s) is always shown so the ruler is visible for empty timelines.
  const last = Math.max(1, Math.ceil(maxSec))
  for (let i = 0; i <= last; i++) ticks.push(i)
  return (
    <div
      className="relative h-full w-9 shrink-0 border-r border-border-subtle"
      data-testid="av-time-ruler"
      aria-label="Time ruler"
    >
      {ticks.map((sec) => (
        <div
          key={`tick-${sec}`}
          className="absolute left-0 right-0 flex items-start gap-1"
          style={{ top: sec * PIXELS_PER_SECOND }}
          data-testid={`av-time-tick-${sec}`}
        >
          <span className="block h-px w-2 bg-text-muted/40 mt-1.5" aria-hidden />
          <span className="text-[10px] font-mono leading-3 mt-0.5 text-text-muted">
            {sec}s
          </span>
        </div>
      ))}
      {/* Minor half-second ticks (visual only, no label) */}
      {ticks.slice(0, -1).map((sec) => (
        <span
          key={`minor-${sec}`}
          className="absolute left-0 block h-px w-1 bg-text-muted/20"
          style={{ top: sec * PIXELS_PER_SECOND + PIXELS_PER_SECOND / 2 + 1 }}
          aria-hidden
        />
      ))}
    </div>
  )
})

TimeRuler.displayName = 'TimeRuler'
